import { Component, OnInit, Inject, ViewChild } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatStepper } from '@angular/material/stepper'
import {
  Channel,
  openOAuthWindow,
  OAuthPopupResult,
  ChannelService,
  CURRENT_ACCOUNT_ID,
} from '@tokeet-frontend/tv3-platform'
import { BehaviorSubject } from 'rxjs'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { ChannelConnectHelperService } from '../channel-connect-helper.service'

const wizardCompleteStepIndex = 2

const authErrorMessages = {
  access_denied: `Airbnb access was denied. Click "Allow" on the Airbnb consent screen to let AdvanceCM manage your listings.`,
  already_connected: `This Airbnb account is already connected to another AdvanceCM account. Disconnect it there first, or contact support.`,
  token_exchange_failed: `Airbnb rejected the connection attempt. Please try again in a few minutes.`,
  no_code: `Airbnb did not return an authorization code. Please try again.`,
  server_error: `Something went wrong on our side while connecting to Airbnb. Please try again or contact support.`,
  closed: `The Airbnb window was closed before the connection finished. Please try again.`,
  timeout: `The Airbnb connection attempt timed out. Please try again.`,
  blocked: `Your browser blocked the Airbnb sign-in popup. Allow popups for this site and try again.`,
  connect_failed: `We could not complete the Airbnb connection. Please try again or contact support.`,
}

@Component({
  selector: 'app-connect-airbnb-wizard',
  templateUrl: './connect-airbnb-wizard.component.html',
  styleUrls: ['./connect-airbnb-wizard.component.scss'],
  host: {
    class: 'modal-content',
  },
})
export class ConnectAirbnbWizardComponent implements OnInit {
  @ViewChild(MatStepper) stepper: MatStepper
  isQuickConnect = true
  isAuthorizing = false
  isAuthorized = false
  authErrorMessage: string = null

  get channel() {
    return this.data.channel
  }

  constructor(
    public dialogRef: MatDialogRef<ConnectAirbnbWizardComponent>,
    private channelHelperService: ChannelConnectHelperService,
    private channelService: ChannelService,
    private amplitudeService: AmplitudeService,
    @Inject(CURRENT_ACCOUNT_ID) private accountId$: BehaviorSubject<number>,
    @Inject(MAT_DIALOG_DATA) public data: { channel: Channel }
  ) {}

  ngOnInit(): void {}

  close() {
    this.dialogRef.close(this.isAuthorized)
  }

  onQuick() {
    this.isQuickConnect = true
    this.startAuthorization()
  }

  onAdvance() {
    this.isQuickConnect = false
    this.authErrorMessage = null
    this.stepper.next()
  }

  onChannel() {
    this.dialogRef.close('channel')
  }

  onDone() {
    this.dialogRef.close('done')
  }

  onConnect() {
    this.startAuthorization(true)
  }

  private startAuthorization(skipImport = false) {
    if (this.isAuthorizing) {
      return
    }
    this.isAuthorizing = true
    this.authErrorMessage = null
    const url = this.channelService.getAirBnBV2AuthorizationUrl(this.accountId$.value, skipImport)
    openOAuthWindow(url, 600, 900).subscribe((result) => {
      this.isAuthorizing = false
      if (result.status === 'success') {
        this.isAuthorized = true
        this.channelHelperService.doAfterConnected(this.channel)
        this.stepper.selectedIndex = wizardCompleteStepIndex
      } else {
        this.onAuthorizationFailed(result)
      }
    })
  }

  private onAuthorizationFailed(result: OAuthPopupResult) {
    const reason = result.status === 'error' ? result.reason || 'connect_failed' : result.status
    this.amplitudeService.logEvent(`airbnb-oauth-failed-${reason}`)
    this.authErrorMessage = authErrorMessages[reason] || authErrorMessages.connect_failed
  }
}
